import { useState } from "react";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { privacyIcon, addButtonIcon, animationIcon, addMusicIcon } from "assets";
import { Checkbox } from "components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "components/ui/radio-group";
import { CirclePlus } from "lucide-react";
import { cn } from "lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import useDialogUploadEditPostStore from "store/dialog/useDialogUploadEditPostContentStore";
import { UploadEditSettingMusicPlayer } from "./UploadEditSettingMusicPlayer";
import { linkButtons } from "utils/enum/LinkButton";
import InputSuggestion from "components/app/function/InputSuggestion";
import DatePicker from "components/app/function/DatePicker";
import TimePicker from "components/app/function/TimePicker";

type MenuItem = {
  label: string;
  value: string;
  icon: string;
};

const settingsMenu: MenuItem[] = [
  { value: "layout", label: "Layout", icon: animationIcon },
  { value: "privacy", label: "Privacy", icon: privacyIcon },
  { value: "add_button", label: "Add Button", icon: addButtonIcon },
  { value: "add_music", label: "Add Music", icon: addMusicIcon },
];

const privacies = ["Allow Comment", "Allow Share", "Allow Add To Favourite"];

const layouts = [
  {
    value: "default",
    label: "Default",
  },
  {
    value: "column",
    label: "Column",
  },
  {
    value: "banner",
    label: "Banner",
  },
];

type UploadSettingProps = {
  onAddMusic: () => void;
};

export default function UploadEditSetting({ onAddMusic }: UploadSettingProps) {
  const {
    post,
    layout,
    whoCanSeeThis,
    selectedPrivacies,
    selectedLinkButton,
    link,
    iosLink,
    androidLink,

    eventTitle,
    eventDate,
    eventStartingTime,
    eventDuration,
    eventLocation,

    professionals,
    applyJobClosingDate,
    applyJobProfessional,

    setLayout,
    setWhoCanSeeThis,
    toggleItemPricacy,
    setSelectedLinkButton,
    setLink,
    setIosLink,
    setAndroidLink,

    setEventTitle,
    setEventDate,
    setEventStartingTime,
    setEventDuration,
    setEventLocation,

    setApplyJobClosingDate,
    setApplyJobProfessional,

    music,
  } = useDialogUploadEditPostStore();

  const [selectedMenu, setSelectedMenu] = useState(post.media_type === "video" ? "privacy" : "layout");

  const filteredMenu = settingsMenu.filter((item) => {
    if (post.media_type === "video" && item.value === "layout") return false;
    else if (post.media_type === "video" && item.value === "add_music") return false;

    return true;
  });

  const renderInputs = () => {
    if (selectedLinkButton === "None") return null;

    if (selectedLinkButton === "Install Now") {
      return (
        <div className="space-y-4 mt-4">
          <div className="space-y-1">
            <Label>iOS Link</Label>
            <Input placeholder="https://apps.apple.com/..." value={iosLink} onChange={(e) => setIosLink(e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label>Android Link</Label>
            <Input placeholder="https://play.google.com/store/..." value={androidLink} onChange={(e) => setAndroidLink(e.target.value)} />
          </div>
        </div>
      );
    } else if (selectedLinkButton === "Join Event") {
      return (
        <div className="space-y-4 mt-4">
          <div className="space-y-1">
            <Label>Event Title</Label>
            <Input placeholder="Title of your event" value={eventTitle} onChange={(e) => setEventTitle(e.target.value)} />
          </div>

          <div className="space-y-1">
            <Label>URL</Label>
            <Input placeholder="Input your url" value={link} onChange={(e) => setLink(e.target.value)} />
          </div>

          <div className="space-y-1 overflow-y-auto">
            <Label>Date</Label>
            <DatePicker className="py-0" onChange={setEventDate} value={eventDate} />
          </div>

          <div className="space-y-1">
            <Label>Start Time</Label>
            <TimePicker placeholder="Input your starting time" value={eventStartingTime} onChange={setEventStartingTime} />
          </div>

          <div className="space-y-1">
            <Label>Duration (Minutes)</Label>
            <Input
              inputMode="numeric"
              pattern="[0-9]*"
              value={eventDuration}
              onChange={(e) => {
                const newValue = e.target.value;
                if (/^\d*$/.test(newValue)) {
                  setEventDuration(newValue);
                }
              }}
              placeholder="Enter number"
            />
          </div>

          <div className="space-y-1">
            <Label>Location</Label>
            <Input placeholder="Input your event location" value={eventLocation} onChange={(e) => setEventLocation(e.target.value)} />
          </div>
        </div>
      );
    } else if (selectedLinkButton === "Apply Job") {
      return (
        <div className="space-y-4 mt-4">
          <div className="space-y-1">
            <Label>Position</Label>

            <InputSuggestion
              value={applyJobProfessional}
              onChange={setApplyJobProfessional}
              placeholder="Position"
              suggestions={professionals.map((professional) => professional.description)}
              className="my-4"
              maxLength={255}
            />
          </div>

          <div className="space-y-1">
            <Label>Url</Label>
            <Input placeholder="Input your url" value={iosLink} onChange={(e) => setIosLink(e.target.value)} />
          </div>

          <div className="space-y-1 overflow-y-auto">
            <Label>Closing Date</Label>
            <DatePicker className="py-0" onChange={setApplyJobClosingDate} value={applyJobClosingDate} placeholder="Pick your closing date" />
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-1 mt-4">
        <Label>Link</Label>
        <Input placeholder="https://example.com" value={link} onChange={(e) => setLink(e.target.value)} />
      </div>
    );
  };

  const renderStyleDefault = (images: string[]) => {
    return images.length === 1 ? (
      <img src={images[0]} alt="post" className="w-full h-full object-cover rounded-lg" />
    ) : (
      <div className="relative w-full aspect-square grid gap-1">
        {images.length === 2 && (
          <div className="grid grid-cols-2 gap-1 w-full h-full">
            {images.map((image, index) => (
              <img key={index} src={image} alt="post" className="w-full h-full object-cover aspect-square " />
            ))}
          </div>
        )}

        {images.length === 3 && (
          <div className="grid grid-cols-2 grid-rows-2 gap-1 w-full h-full">
            <div className="col-span-1 row-span-2 overflow-hidden flex">
              <img src={images[0]} alt="post" className="w-full h-full object-cover aspect-square " />
            </div>
            <div className="col-span-1 row-span-1 overflow-hidden flex">
              <img src={images[1]} alt="post" className="w-full h-full object-cover aspect-square " />
            </div>
            <div className="col-span-1 row-span-1 overflow-hidden flex">
              <img src={images[2]} alt="post" className="w-full h-full object-cover aspect-square " />
            </div>
          </div>
        )}

        {images.length === 4 && (
          <div className="grid grid-cols-2 grid-rows-2 gap-1 w-full h-full">
            {images.map((image, index) => (
              <img key={index} src={image} alt="post" className="w-full h-full object-cover aspect-square " />
            ))}
          </div>
        )}

        {images.length > 4 && (
          <div className="grid grid-cols-2 grid-rows-2 gap-1 w-full h-full relative">
            {images.slice(0, 3).map((image, index) => (
              <img key={index} src={image} alt="post" className="w-full h-full object-cover aspect-square " />
            ))}
            <div className="relative w-full h-full overflow-hidden flex">
              <img src={images[3]} alt="post" className="w-full h-full object-cover aspect-square " />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white font-bold text-xl ">+{images.length - 4}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStyleColumn = (images: string[]) => {
    return images.length === 1 ? (
      <img src={images[0]} alt="post" className="w-full h-full object-cover rounded-lg" />
    ) : (
      <div className="relative w-full aspect-square grid gap-1 overflow-hidden rounded-lg">
        {images.length === 1 && <img src={images[0]} alt="post" className="w-full h-full object-cover rounded-lg" />}

        {images.length === 2 && (
          <div className="grid grid-cols-2 gap-1 w-full h-full">
            <div className="overflow-hidden rounded-lg mb-5">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg mt-5">
              <img src={images[1]} alt="post" className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        {images.length === 3 && (
          <div className="grid grid-cols-3 gap-1 w-full h-full">
            <div className="overflow-hidden rounded-lg mb-5">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg mt-5">
              <img src={images[1]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg mb-5">
              <img src={images[2]} alt="post" className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        {images.length === 4 && (
          <div className="grid grid-cols-4 gap-1 w-full h-full">
            <div className="overflow-hidden rounded-lg mb-5">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg mt-5">
              <img src={images[1]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg mb-5">
              <img src={images[2]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="overflow-hidden rounded-lg mt-5">
              <img src={images[3]} alt="post" className="w-full h-full object-cover" />
            </div>
          </div>
        )}

        {images.length > 4 && (
          <div className="grid grid-cols-4 gap-1 w-full h-full relative">
            {images.slice(0, 3).map((image, index) => (
              <div key={index} className={`overflow-hidden rounded-lg ${index % 2 === 0 ? "mb-5" : "mt-5"}`}>
                <img src={image} alt="post" className="w-full h-full object-cover" />
              </div>
            ))}
            <div className="relative overflow-hidden rounded-lg mt-5">
              <img src={images[3]} alt="post" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white font-bold text-xl rounded-lg">+{images.length - 4}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderStyleBanner = (images: string[]) => {
    return images.length === 1 ? (
      <img src={images[0]} alt="post" className="w-full h-full object-cover rounded-lg" />
    ) : (
      <div className="relative w-full aspect-square grid grid-rows-[55%_45%] gap-1 overflow-hidden rounded-lg bg-gray-100">
        {images.length === 1 && (
          <div className="w-full h-full overflow-hidden rounded-lg">
            <img src={images[0]} alt="post" className="w-full h-full object-cover" />
          </div>
        )}

        {images.length === 2 && (
          <>
            <div className="w-full h-full overflow-hidden rounded-lg">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="w-full h-full overflow-hidden flex items-center justify-center">
              <div className="w-[90%] h-[90%] overflow-hidden rounded-lg rounded-b-lg">
                <img src={images[1]} alt="post" className="w-full h-full object-cover" />
              </div>
            </div>
          </>
        )}

        {images.length === 3 && (
          <>
            <div className="w-full h-full overflow-hidden rounded-lg">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-2 w-full h-full gap-1">
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[1]} alt="post" className="w-full h-full object-cover" />
              </div>
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[2]} alt="post" className="w-full h-full object-cover" />
              </div>
            </div>
          </>
        )}

        {images.length === 4 && (
          <>
            <div className="w-full h-full overflow-hidden rounded-lg">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-3 w-full h-full gap-1">
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[1]} alt="post" className="w-full h-full object-cover" />
              </div>
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[2]} alt="post" className="w-full h-full object-cover" />
              </div>
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[3]} alt="post" className="w-full h-full object-cover" />
              </div>
            </div>
          </>
        )}

        {images.length > 4 && (
          <>
            <div className="w-full h-full overflow-hidden rounded-lg">
              <img src={images[0]} alt="post" className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-3 w-full h-full gap-1">
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[1]} alt="post" className="w-full h-full object-cover" />
              </div>
              <div className="w-full h-full overflow-hidden rounded-lg">
                <img src={images[2]} alt="post" className="w-full h-full object-cover" />
              </div>
              <div className="relative w-full h-full overflow-hidden rounded-lg">
                <img src={images[3]} alt="post" className="w-full h-full object-cover" />

                <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center text-white font-bold text-xl rounded-lg">+{images.length - 4}</div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col md:flex-row gap-6">
      {/* Sidebar */}
      <aside className="w-full md:w-64 border rounded-xl bg-background p-4 shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Settings</h2>
        <nav className="space-y-1">
          {filteredMenu.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={item.value}
                onClick={() => setSelectedMenu(item.value)}
                className={`w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition ${
                  selectedMenu === item.value ? "bg-muted text-primary font-medium" : "hover:bg-muted"
                }`}
              >
                <img src={Icon} alt={item.label} draggable="false" className={`w-4 h-4 dark:brightness-0 dark:invert`} />

                {item.label}
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Content */}
      <Card className="flex-1">
        <CardContent className="p-4 space-y-6 w-full">
          {/* Forms */}

          {selectedMenu === "layout" && (
            <div className="space-y-4 w-full">
              <RadioGroup value={layout} onValueChange={setLayout} className="flex gap-3 w-full">
                {layouts.map((element) => (
                  <label
                    key={element.value}
                    htmlFor={element.value}
                    className={cn(
                      "flex-1 text-center cursor-pointer px-5 py-2 rounded-lg border text-sm font-medium transition-colors",
                      layout === element.value ? "border-primary text-primary" : "border-muted text-muted-foreground hover:border-border"
                    )}
                  >
                    <RadioGroupItem value={element.value} id={element.value} className="hidden" />
                    {element.label}
                  </label>
                ))}
              </RadioGroup>

              {layout === "default"
                ? renderStyleDefault(post.post_attachments.map((att: any) => att.attachment_media_image_file_src))
                : layout === "column"
                ? renderStyleColumn(post.post_attachments.map((att: any) => att.attachment_media_image_file_src))
                : renderStyleBanner(post.post_attachments.map((att: any) => att.attachment_media_image_file_src))}
            </div>
          )}

          {selectedMenu === "privacy" && (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-semibold">Post Privacy</h3>
              </div>

              <div className="space-y-3 pl-3">
                {privacies.map((item) => (
                  <div key={item} className="flex items-center space-x-2">
                    <Checkbox id={item} checked={selectedPrivacies.includes(item)} onCheckedChange={() => toggleItemPricacy(item)} />
                    <Label htmlFor={item}>{item}</Label>
                  </div>
                ))}
              </div>

              <div>
                <h3 className="text-sm font-semibold">Who can see this</h3>
              </div>

              <RadioGroup value={whoCanSeeThis} onValueChange={setWhoCanSeeThis} className="pl-3">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="everyone" id="everyone" />
                  <Label htmlFor="everyone">Everyone</Label>
                </div>

                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="friends" id="friends" />
                  <Label htmlFor="friends">Friends</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {selectedMenu === "add_button" && (
            <div className="space-y-6">
              <div className="space-y-2">
                <Label>Add Button</Label>
                <Select value={selectedLinkButton} onValueChange={setSelectedLinkButton}>
                  <SelectTrigger className="w-full">
                    <div className="flex items-center justify-between w-full">
                      <SelectValue />
                      {selectedLinkButton !== "None" && (
                        <div
                          className="ml-2 px-3 py-1 rounded-sm text-white text-xs font-medium mr-2"
                          style={{
                            backgroundColor: linkButtons.find((b) => b.name === selectedLinkButton)?.color || "#ccc",
                          }}
                        >
                          {selectedLinkButton}
                        </div>
                      )}
                    </div>
                  </SelectTrigger>

                  <SelectContent>
                    {linkButtons.map((btn) => (
                      <SelectItem key={btn.name} value={btn.name}>
                        {btn.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {renderInputs()}
            </div>
          )}

          {selectedMenu === "add_music" && (
            <div className="space-y-4">
              {music ? (
                <UploadEditSettingMusicPlayer music={music} onAddMusic={onAddMusic} />
              ) : (
                <Button className="gap-3" onClick={onAddMusic}>
                  <CirclePlus /> Add Music
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

